import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatAxiosError } from "@/lib/api";
import { useCapabilities } from "@/context/CapabilitiesContext";
import {
  ArrowLeft, Briefcase, Plus, UserPlus, FileArrowUp, Paperclip, X,
  DotsThreeVertical, User as UserIcon, MapPin, Buildings, XCircle, LockKey,
} from "@phosphor-icons/react";

const STAGES = [
  { key: "applied", label: "Applied", color: "bg-blue-50 border-blue-200" },
  { key: "screening", label: "Screening", color: "bg-amber-50 border-amber-200" },
  { key: "interview", label: "Interview", color: "bg-purple-50 border-purple-200" },
  { key: "offer", label: "Offer", color: "bg-emerald-50 border-emerald-200" },
  { key: "hired", label: "Hired", color: "bg-green-100 border-green-400" },
  { key: "rejected", label: "Rejected", color: "bg-red-50 border-red-200" },
  { key: "withdrawn", label: "Withdrawn", color: "bg-gray-50 border-gray-200" },
];

const SOURCE_COLORS = {
  direct: "bg-slate-100 text-slate-800 border-slate-300",
  referral: "bg-purple-100 text-purple-800 border-purple-300",
  import: "bg-gray-100 text-gray-700 border-gray-300",
};

export default function JobApplicants() {
  const { jid } = useParams();
  const nav = useNavigate();
  const { can } = useCapabilities();
  const [job, setJob] = useState(null);
  const [grouped, setGrouped] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [draggedId, setDraggedId] = useState(null);

  const canManage = can("manage_applications");
  const canClose = can("close_jobs");

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 3500); };

  const load = async () => {
    setLoading(true);
    try {
      const [jr, ar] = await Promise.all([
        api.get(`/jobs/${jid}`),
        api.get(`/jobs/${jid}/applications`),
      ]);
      setJob(jr.data);
      setGrouped(ar.data.grouped || {});
      setTotal(ar.data.total || 0);
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jid]);

  const onDragStart = (e, appId) => {
    if (!canManage) return;
    setDraggedId(appId);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { if (canManage) e.preventDefault(); };
  const onDrop = async (e, stage) => {
    e.preventDefault();
    if (!canManage || !draggedId) return;
    try {
      await api.patch(`/applications/${draggedId}`, { status: stage });
      setDraggedId(null);
      await load();
      flash("ok", `Moved to ${stage}.`);
    } catch (err) {
      setDraggedId(null);
      flash("error", formatAxiosError(err));
    }
  };

  const reopen = async () => {
    try { await api.post(`/jobs/${jid}/reopen`); await load(); flash("ok", "Job reopened."); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!job) return <div className="p-8">Job not found.</div>;

  const isClosed = job.status === "closed";

  return (
    <div className="p-8 lg:p-12">
      <button onClick={() => nav("/jobs")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
        <ArrowLeft size={14} weight="bold" /> Back to Jobs
      </button>

      {/* Header */}
      <div className="mb-6 border border-border p-6 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="eyebrow mb-1 flex items-center gap-2"><Briefcase size={12} weight="bold" /> Applicants pipeline</div>
            <h1 className="font-display text-3xl font-black leading-tight">{job.title}</h1>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
              {job.department && <span className="flex items-center gap-1"><Buildings size={12} weight="bold" /> {job.department}</span>}
              {job.location && <span className="flex items-center gap-1"><MapPin size={12} weight="bold" /> {job.location}</span>}
              <span>{job.employment_type} · {job.experience_level}</span>
              <span className={`px-2 py-0.5 border font-semibold uppercase ${isClosed ? "bg-red-100 text-red-700 border-red-300" : "bg-green-100 text-green-800 border-green-400"}`}>{job.status}</span>
              <span className="font-semibold">{total} applicant{total === 1 ? "" : "s"}</span>
            </div>
            {isClosed && job.closed_reason && (
              <div className="mt-3 text-xs text-red-700 border border-red-300 bg-red-50 px-2 py-1 inline-block">
                Closed: {job.closed_reason}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => nav(`/jobs/${jid}`)} className="px-3 py-2 text-xs border border-border hover:bg-muted">
              Edit job details
            </button>
            {canManage && !isClosed && (
              <button onClick={() => setAddOpen(true)} className="btn-primary flex items-center gap-2" data-testid="add-applicant-btn">
                <UserPlus size={14} weight="bold" /> Add applicant
              </button>
            )}
            {canClose && !isClosed && (
              <button onClick={() => setCloseOpen(true)} className="px-3 py-2 text-xs border border-red-500 text-red-600 hover:bg-red-50 flex items-center gap-2" data-testid="close-job-btn">
                <LockKey size={14} weight="bold" /> Close job
              </button>
            )}
            {isClosed && can("manage_jobs") && (
              <button onClick={reopen} className="px-3 py-2 text-xs border border-green-500 text-green-700 hover:bg-green-50" data-testid="reopen-job-btn">
                Reopen job
              </button>
            )}
          </div>
        </div>
      </div>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-green-500 bg-green-50 text-green-700" : "border-red-500 bg-red-50 text-red-700"}`}>{msg.text}</div>
      )}

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {STAGES.map(stage => {
          const items = grouped[stage.key] || [];
          return (
            <div key={stage.key}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, stage.key)}
              className={`border ${stage.color} p-2 min-h-[400px]`}
              data-testid={`lane-${stage.key}`}>
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="font-semibold text-xs uppercase tracking-widest">{stage.label}</div>
                <span className="text-xs bg-white border border-border px-1.5">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map(a => (
                  <ApplicantCard key={a.id} a={a} canManage={canManage}
                    onDragStart={onDragStart}
                    onOpen={() => nav(`/applications/${a.id}`)} />
                ))}
                {items.length === 0 && (
                  <div className="text-[11px] text-muted-foreground text-center py-4">—</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {addOpen && <AddApplicantModal jid={jid} onClose={() => setAddOpen(false)} onSaved={async () => { setAddOpen(false); await load(); flash("ok", "Applicant added."); }} />}
      {closeOpen && <CloseJobModal jid={jid} onClose={() => setCloseOpen(false)} onDone={async () => { setCloseOpen(false); await load(); flash("ok", "Job closed."); }} />}
    </div>
  );
}

function ApplicantCard({ a, canManage, onDragStart, onOpen }) {
  const rating = useMemo(() => {
    const ivs = a.interviews || [];
    return { interviews: ivs.length };
  }, [a]);
  return (
    <div
      draggable={canManage}
      onDragStart={(e) => onDragStart(e, a.id)}
      onClick={onOpen}
      className="border border-border bg-white p-2 hover:border-[#002FA7] hover:shadow-sm cursor-pointer transition-all"
      data-testid={`applicant-card-${a.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="font-semibold text-sm leading-tight truncate">{a.candidate_name}</div>
        <span className={`shrink-0 text-[9px] font-semibold uppercase px-1 py-0.5 border ${SOURCE_COLORS[a.source] || SOURCE_COLORS.direct}`}>{a.source}</span>
      </div>
      <div className="text-xs text-muted-foreground truncate">{a.candidate_email}</div>
      {a.referrer && (
        <div className="text-[10px] text-purple-700 mt-1 truncate">↩ {a.referrer.name}</div>
      )}
      <div className="mt-1.5 text-[10px] text-muted-foreground">
        {new Date(a.created_at).toLocaleDateString()}
      </div>
    </div>
  );
}

function CloseJobModal({ jid, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/jobs/${jid}/close`, { reason }); onDone(); }
    catch (e) { setErr(formatAxiosError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-lg font-black">Close this job</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-muted-foreground">This will stop accepting new applications. You can reopen the job later.</p>
          {err && <div className="text-xs text-red-700 border border-red-500 bg-red-50 px-3 py-2">{err}</div>}
          <label className="block">
            <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Reason (optional)</span>
            <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Filled internally" data-testid="close-reason" />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="confirm-close">{busy ? "Closing…" : "Close job"}</button>
        </div>
      </div>
    </div>
  );
}

function AddApplicantModal({ jid, onClose, onSaved }) {
  const [form, setForm] = useState({
    candidate_name: "", candidate_email: "", candidate_phone: "",
    resume_file_id: "", attachments: [], notes: "", source: "direct",
  });
  const [resumeMeta, setResumeMeta] = useState(null);
  const [attMetas, setAttMetas] = useState([]);
  const [uploadingR, setUploadingR] = useState(false);
  const [uploadingA, setUploadingA] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const upload = async (file, setBusyU, cb) => {
    setBusyU(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      cb(r.data);
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setBusyU(false); }
  };

  const humanSize = (b) => {
    if (b == null) return "";
    const u = ["B", "KB", "MB"]; let i = 0, n = b;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
  };

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post(`/jobs/${jid}/applications`, {
        candidate_name: form.candidate_name.trim(),
        candidate_email: form.candidate_email.trim(),
        candidate_phone: form.candidate_phone.trim(),
        resume_file_id: form.resume_file_id || "",
        attachments: form.attachments,
        notes: form.notes.trim(),
        source: form.source,
      });
      onSaved();
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-xl font-black">Add applicant</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-3">
          {err && <div className="text-xs text-red-700 border border-red-500 bg-red-50 px-3 py-2">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Candidate name*"><input className="input" value={form.candidate_name} onChange={(e) => setForm({ ...form, candidate_name: e.target.value })} data-testid="app-name" /></Field>
            <Field label="Email*"><input className="input" type="email" value={form.candidate_email} onChange={(e) => setForm({ ...form, candidate_email: e.target.value })} data-testid="app-email" /></Field>
            <Field label="Phone"><input className="input" value={form.candidate_phone} onChange={(e) => setForm({ ...form, candidate_phone: e.target.value })} /></Field>
            <Field label="Source">
              <select className="input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                <option value="direct">Direct</option>
                <option value="import">Import</option>
              </select>
            </Field>
          </div>
          <Field label="Resume">
            {resumeMeta ? (
              <div className="flex items-center gap-2 border border-border px-3 py-2 bg-muted/30 text-sm">
                <Paperclip size={14} weight="bold" className="text-[#002FA7]" />
                <span className="flex-1 truncate">{resumeMeta.filename}</span>
                <span className="text-xs text-muted-foreground">{humanSize(resumeMeta.size_bytes)}</span>
                <button onClick={() => { setResumeMeta(null); setForm(f => ({ ...f, resume_file_id: "" })); }} className="text-red-600 text-xs">Remove</button>
              </div>
            ) : (
              <label className="flex items-center gap-2 border border-dashed border-border px-3 py-3 hover:bg-muted/30 cursor-pointer text-sm">
                <FileArrowUp size={16} weight="bold" className="text-[#002FA7]" />
                <span>{uploadingR ? "Uploading resume…" : "Upload resume (PDF, DOC, DOCX…)"}</span>
                <input type="file" className="hidden" accept=".pdf,.doc,.docx,.rtf,.txt" onChange={(e) => {
                  const f = e.target.files && e.target.files[0]; if (!f) return;
                  upload(f, setUploadingR, (d) => {
                    const fid = d.file_id || d.id;
                    setForm(x => ({ ...x, resume_file_id: fid }));
                    setResumeMeta({ file_id: fid, filename: d.filename || f.name, size_bytes: d.size_bytes || f.size });
                  });
                }} data-testid="app-resume" />
              </label>
            )}
          </Field>
          <Field label="Other documents (portfolio, cover letter, references…)">
            {attMetas.length > 0 && (
              <ul className="space-y-1 mb-2">
                {attMetas.map(m => (
                  <li key={m.file_id} className="flex items-center gap-2 border border-border px-3 py-1.5 bg-muted/30 text-xs">
                    <Paperclip size={12} weight="bold" />
                    <span className="flex-1 truncate">{m.filename}</span>
                    <span className="text-muted-foreground">{humanSize(m.size_bytes)}</span>
                    <button onClick={() => { setAttMetas(prev => prev.filter(x => x.file_id !== m.file_id)); setForm(x => ({ ...x, attachments: x.attachments.filter(a => a !== m.file_id) })); }} className="text-red-600">Remove</button>
                  </li>
                ))}
              </ul>
            )}
            <label className="flex items-center gap-2 border border-dashed border-border px-3 py-2 hover:bg-muted/30 cursor-pointer text-sm">
              <Paperclip size={14} weight="bold" />
              <span>{uploadingA ? "Uploading…" : "Add another document"}</span>
              <input type="file" className="hidden" onChange={(e) => {
                const f = e.target.files && e.target.files[0]; if (!f) return;
                upload(f, setUploadingA, (d) => {
                  const fid = d.file_id || d.id;
                  setForm(x => ({ ...x, attachments: [...x.attachments, fid] }));
                  setAttMetas(prev => [...prev, { file_id: fid, filename: d.filename || f.name, size_bytes: d.size_bytes || f.size }]);
                });
                e.target.value = "";
              }} data-testid="app-att" />
            </label>
          </Field>
          <Field label="Notes"><textarea className="input min-h-[80px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="save-app">{busy ? "Saving…" : "Add applicant"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="block">
      <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">{label}</span>
      {children}
    </div>
  );
}
